/**
 * Canvas 성능 최적화 유틸리티
 * willReadFrequently 속성을 설정하여 getImageData() 성능 향상
 */

let canvas2DWillReadPatchInstalled = false;

/**
 * 2D 컨텍스트를 옵션 없이 생성하는 라이브러리(OpenLayers 등)는 이후 getImageData 반복 시
 * Chrome이 `willReadFrequently` 설정을 권고한다. 힌트는 최초 getContext 시에만 적용되므로,
 * 명시하지 않은 `getContext('2d')` 호출에 `willReadFrequently: true`를 기본으로 붙인다.
 * `willReadFrequently: false` 를 넘긴 호출은 그대로 둔다.
 *
 * 앱 진입점(main.js)에서 Vue·지도 초기화보다 먼저 한 번 호출할 것.
 */
export function installCanvas2DWillReadFrequentlyDefault() {
  if (canvas2DWillReadPatchInstalled || typeof HTMLCanvasElement === 'undefined') {
    return;
  }
  canvas2DWillReadPatchInstalled = true;
  const orig = HTMLCanvasElement.prototype.getContext;
  HTMLCanvasElement.prototype.getContext = function (type, options) {
    if (type === '2d') {
      const opts =
        options !== undefined && options !== null && typeof options === 'object'
          ? { ...options }
          : {};
      if (opts.willReadFrequently === undefined) {
        opts.willReadFrequently = true;
      }
      return orig.call(this, type, opts);
    }
    return orig.call(this, type, options);
  };
}

/**
 * 특정 요소 내의 모든 Canvas에 성능 최적화 적용
 * @param {string|HTMLElement} target - 대상 요소 (ID 문자열 또는 DOM 요소)
 * @param {string} componentName - 컴포넌트 이름 (로깅용)
 * @returns {boolean} 최적화 성공 여부
 */
export function optimizeCanvasPerformance(target, componentName = 'Unknown') {
  try {
    // 대상 요소 찾기
    const targetElement = typeof target === 'string' ? document.getElementById(target) : target;
    if (!targetElement) {
      console.warn(`[${componentName}] 대상 요소를 찾을 수 없습니다:`, target);
      return false;
    }

    // Canvas 요소들 찾기
    const canvasElements = targetElement.querySelectorAll('canvas');
    if (canvasElements.length === 0) {
      console.log(`[${componentName}] 최적화할 Canvas 요소가 없습니다`);
      return true;
    }

    // 각 Canvas 요소에 최적화 적용
    let optimizedCount = 0;
    canvasElements.forEach(canvas => {
      if (canvas.getContext && !canvas._optimized) {
        try {
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          canvas._optimized = true;
          optimizedCount++;
        } catch (error) {
          console.warn(`[${componentName}] Canvas 최적화 실패:`, error);
        }
      }
    });

    if (optimizedCount > 0) {
      console.log(`[${componentName}] Canvas 성능 최적화 완료: ${optimizedCount}개 최적화됨`);
    }
    return true;

  } catch (error) {
    console.error(`[${componentName}] Canvas 성능 최적화 설정 실패:`, error);
    return false;
  }
}

/**
 * Vue 컴포넌트에서 사용하는 최적화 함수 (Vue 인스턴스 필요)
 * @param {Vue} vm - Vue 컴포넌트 인스턴스
 * @param {string|HTMLElement} target - 대상 요소
 * @param {string} componentName - 컴포넌트 이름
 */
export function optimizeCanvasPerformanceVue(vm, target, componentName = 'Unknown') {
  if (!vm || !vm.$nextTick) {
    console.warn(`[${componentName}] Vue 인스턴스가 필요합니다`);
    return;
  }

  vm.$nextTick(() => {
    optimizeCanvasPerformance(target, componentName);
  });
}

/**
 * 맵 렌더링 완료 후 Canvas 최적화 (OpenLayers 맵용)
 * @param {Object} map - OpenLayers 맵 인스턴스
 * @param {string|HTMLElement} target - 대상 요소
 * @param {string} componentName - 컴포넌트 이름
 */
export function optimizeCanvasPerformanceOnMapRender(map, target, componentName = 'Unknown') {
  if (!map || !map.on) {
    console.warn(`[${componentName}] OpenLayers 맵 인스턴스가 필요합니다`);
    return;
  }

  map.on('rendercomplete', () => {
    optimizeCanvasPerformance(target, componentName);
  });
}

export default {
  installCanvas2DWillReadFrequentlyDefault,
  optimizeCanvasPerformance,
  optimizeCanvasPerformanceVue,
  optimizeCanvasPerformanceOnMapRender
};
